import { resolveCsrfToken, sendJsonMutation } from '../../components/write-request.js';
import { canAnimateElement, prefersReducedMotion, readCssTimeMs } from '../../components/motion-policy.js';
import { cancelAnimations, playAnimation, settleAnimations } from '../../components/motion-runner.js';
import { isRankingBusy, isRankingStale, markRankingStale, setRankingBusy } from './busy-lock.js';
import { initOrderedReorder } from './ordered-reorder.js';

const SEARCH_DEBOUNCE_MS = 250;
// The row that received a game lights up long enough to be found by eye, matching the CSS animation.
const FLASH_MS = 1000;
const SAVED_VISIBLE_MS = 1800;
// A confirmation that names the game and the slot is read, not glanced at, so it stays longer.
const NAMED_VISIBLE_MS = 3000;
// A failure names the operation, in the same words the server uses when it is the one that failed.
const ADD_FAILURE_MESSAGE = 'Could not add the game. Try again.';
const REMOVE_FAILURE_MESSAGE = 'Could not remove the game. Try again.';
const RENAME_FAILURE_MESSAGE = 'Could not rename the category. Try again.';
const MOVE_FAILURE_MESSAGE = 'Could not save the new order. Try again.';
const REFRESH_FAILURE_MESSAGE = 'Saved, but the list could not be refreshed. Reload the page.';
const SEARCH_FAILURE_MESSAGE = 'Could not search your library. Try again.';
const PAGE_FAILURE_MESSAGE = 'Could not load that results page. Try again.';
const SORT_FAILURE_MESSAGE = 'Could not sort your library. Try again.';
// The order the server applies without `picker_sort`, so the address leaves it out.
const DEFAULT_PICKER_SORT = 'rating';

/**
 * The editing mode of a ranking. Two jobs, and they are the same job twice: aim the picker at a position,
 * and store every operation the moment it happens. There is no local draft and no `Save` - `Done` only
 * leaves the mode - so nothing here has to reconcile a dirty state across ten slots.
 *
 * The right panel is not a form of its own, it is a tool aimed at one slot: there is always exactly one
 * aimed at, the marked row and the panel heading always name the same one, and placing a game moves the
 * aim to the next free slot so the work becomes `find - add - find - add` instead of a four-step ritual.
 *
 * The position choice, search and picker pagination work without this module: they are ordinary links and
 * a GET form the server honours. This only removes the round trips.
 */
export function initRankingEditMode(root = globalThis.document, fetchImpl = globalThis.fetch, options = {}) {
    const editRoot = root?.querySelector?.('[data-rankings-edit]');
    if (!(editRoot instanceof HTMLElement)) {
        return;
    }

    const timerApi = options.timerApi ?? globalThis;
    const historyApi = options.historyApi ?? globalThis.history;
    const parseHtml = options.parseHtml
        ?? ((html) => new globalThis.DOMParser().parseFromString(html, 'text/html'));
    // The first placement into a not-yet-saved proposal creates the row and changes the URL, so that one
    // submit stays a navigation instead of being answered in place.
    const navigates = editRoot.hasAttribute('data-rankings-edit-navigates');
    const searchForm = root.querySelector('[data-rankings-search-form]');
    const searchInput = root.querySelector('[data-rankings-search-input]');
    const searchClear = root.querySelector('[data-rankings-search-clear]');
    const sortSelect = root.querySelector('[data-rankings-picker-sort]');
    const heading = root.querySelector('[data-rankings-assign-heading]');
    const feedback = root.querySelector('[data-rankings-picker-feedback]');
    const baseUrl = String(searchForm?.getAttribute('action') ?? '').trim();

    let query = String(searchInput?.value ?? '').trim();
    let pickerSort = String(sortSelect?.value ?? '').trim() || DEFAULT_PICKER_SORT;
    // The order the list on screen was read in. A failed read puts the select back on it: left on the
    // new value, choosing that option again would fire no `change` and the retry would be unreachable.
    let shownSort = pickerSort;
    let pickerPage = toPosition(editRoot.dataset.rankingsPickerPage) ?? 1;
    let selectedPosition = toPosition(editRoot.dataset.rankingsSelectedPosition);
    let debounceHandle = null;
    let pickerPageHandle = null;
    let refreshGeneration = 0;
    let pickerAnimations = [];
    const statusElement = root.querySelector('[data-rankings-status]');
    // The picker header's copy of the status, for whoever is scrolled down to the candidates. Written only
    // here, together with the original, so the two cannot say different things.
    const statusMirror = root.querySelector('[data-rankings-status-mirror]');
    const statusSurfaces = [statusElement, statusMirror].filter((element) => element instanceof HTMLElement);
    let statusExpiryHandle = null;
    let statusAnimations = [];
    let statusRevision = 0;
    // Counts the positions chosen by hand. A re-read compares it with the count at its start to know whether
    // the user aimed somewhere while it was in flight - the position it asked the server about cannot tell,
    // because an `Add` or a `Remove` asks for none at all.
    let selectionRevision = 0;
    // Counts focus moves inside the editor, so an operation can tell whether the user went somewhere else
    // while it was in flight - even to a control its re-read then replaced.
    let focusRevision = 0;
    let placingFocus = false;

    /**
     * The one save-status surface of this screen, and this module owns it: the reorder is handed the
     * function rather than resolving the element itself, so a drag and a placement cannot report the same
     * class of event in two places and in two wordings.
     *
     * A confirmation that stays stops being feedback and becomes part of the layout, so `Saved` expires. An
     * error does not: it is the only thing on screen saying the operation has to be retried.
     *
     * `message` is a string, or `{ target, title }` for a confirmation that names a game: the target comes
     * first and stays whole, the title gets a span of its own that CSS may cut to one line. The live region
     * still holds the whole text.
     */
    const showStatus = (message, state, visibleMs = SAVED_VISIBLE_MS) => {
        if (!(statusElement instanceof HTMLElement)) {
            return;
        }

        if (statusExpiryHandle !== null) {
            timerApi.clearTimeout(statusExpiryHandle);
            statusExpiryHandle = null;
        }
        const revision = ++statusRevision;
        const text = typeof message === 'string' ? message : message.target + ': ' + message.title;
        cancelAnimations(statusAnimations);
        statusAnimations = [];

        const animateSurfaces = async (keyframes, options) => {
            statusAnimations = statusSurfaces.map((element) => playAnimation(element, keyframes, options));
            await settleAnimations(statusAnimations);
            if (revision !== statusRevision) {
                return false;
            }
            cancelAnimations(statusAnimations);
            statusAnimations = [];

            return true;
        };

        const transition = async () => {
            const animate = canAnimateElement(statusElement);
            if (statusElement.textContent !== '' && animate) {
                const settled = await animateSurfaces([
                    { opacity: 1, transform: 'translateY(0)' },
                    { opacity: 0, transform: 'translateY(-3px)' },
                ], {
                    duration: readCssTimeMs(statusElement, '--rankings-status-exit-duration', 110),
                    easing: 'ease-in',
                    fill: 'both',
                });
                if (!settled) {
                    return;
                }
            }

            statusSurfaces.forEach((element) => {
                if (typeof message === 'string') {
                    element.textContent = message;
                } else {
                    const target = root.createElement('span');
                    target.className = 'rankings-panel-status-target';
                    target.textContent = message.target + ':';
                    const title = root.createElement('span');
                    title.className = 'rankings-panel-status-title';
                    title.textContent = message.title;
                    // The space is for the text a screen reader reads; the layout spaces the two by a gap.
                    element.replaceChildren(target, ' ', title);
                }
                element.setAttribute('data-state', text === '' ? 'idle' : state);
            });

            if (text !== '' && animate) {
                const settled = await animateSurfaces([
                    { opacity: 0, transform: 'translateY(3px)' },
                    { opacity: 1, transform: 'translateY(0)' },
                ], {
                    duration: readCssTimeMs(statusElement, '--rankings-status-enter-duration', 150),
                    easing: 'cubic-bezier(0.16, 1, 0.3, 1)',
                    fill: 'both',
                });
                if (!settled) {
                    return;
                }
            }

            if (state !== 'saved' || text === '' || revision !== statusRevision) {
                return;
            }

            statusExpiryHandle = timerApi.setTimeout(() => {
                statusExpiryHandle = null;
                showStatus('', 'idle');
            }, visibleMs);
        };

        void transition();
    };

    let reorderController = null;
    // The arrow a keyboard move was made with, so the re-read after it can hand focus back to that arrow.
    // A drag has no control to return to.
    let moveFocus = null;
    const initReorder = () => {
        reorderController = initOrderedReorder(root, fetchImpl, {
            refreshAfterSave: async () => {
                const pending = moveFocus;
                moveFocus = null;
                const result = await refresh(true);
                if (result === 'ok' && pending !== null) {
                    restoreFocus(moveArrow(pending.userGameId, pending.delta), pending.focusAtStart);
                }

                return result;
            },
            showStatus,
        });
    };

    /** Picker read failures only. Everything stored reports through `showStatus`. */
    const showFeedback = (message, isError) => {
        if (!(feedback instanceof HTMLElement)) {
            return;
        }

        feedback.textContent = message;
        feedback.hidden = message === '';
        // `ui-inline-feedback` is toned by `data-tone`; a class of its own would render unstyled.
        feedback.setAttribute('data-tone', isError === true ? 'error' : 'info');
    };

    /**
     * Explicit parameters rather than the live state, because the two come apart: the markup on screen
     * belongs to the request that fetched it, while the chosen position may have moved on since. The address
     * bar has to describe the combination that actually settled, not whichever half is newer.
     */
    const buildUrl = ({ pickerQuery, sort, page, position }) => {
        const parameters = ['edit=1'];
        if (pickerQuery !== '') {
            parameters.push('picker_q=' + encodeURIComponent(pickerQuery));
        }
        if (sort !== DEFAULT_PICKER_SORT) {
            parameters.push('picker_sort=' + encodeURIComponent(sort));
        }
        if (page > 1) {
            parameters.push('picker_page=' + page);
        }
        if (position !== null) {
            parameters.push('position=' + position);
        }

        return baseUrl + '?' + parameters.join('&');
    };

    const currentParameters = () => ({
        pickerQuery: query,
        sort: pickerSort,
        page: pickerPage,
        position: selectedPosition,
    });

    /** The one place the aimed position is written, so the closure and the attribute cannot disagree. */
    const rememberSelection = (position) => {
        selectedPosition = position;
        // No aimed position means every slot is taken. A search field that cannot place what it finds is a
        // dead end, so it goes away until removing a game gives it a target again.
        if (searchForm instanceof HTMLElement) {
            searchForm.hidden = position === null;
        }
        if (position === null) {
            delete editRoot.dataset.rankingsSelectedPosition;

            return;
        }
        editRoot.dataset.rankingsSelectedPosition = String(position);
    };

    /**
     * Shows where a placed game went: the row that received it, not the next target. Motion is opted out of
     * by `prefers-reduced-motion` in CSS; the class is inert there.
     */
    const flashRow = (position) => {
        const row = slotAt(root, position);
        if (!(row instanceof HTMLElement)) {
            return;
        }

        row.classList.add('is-filled-flash');
        timerApi.setTimeout(() => row.classList.remove('is-filled-flash'), FLASH_MS);
    };

    /**
     * The control an operation was made with lives in a block its re-read replaces, so focus falls to the
     * page. It is handed to `target` - only then: focus moved since `focusAtStart` is the user's newer
     * choice, wherever it went. A plain focus, so the browser and the pinned-header correction below keep it
     * in view.
     */
    const restoreFocus = (target, focusAtStart) => {
        const active = root.activeElement;
        if (
            focusRevision !== focusAtStart
            || (active instanceof HTMLElement && active !== root.body && active.isConnected)
        ) {
            return;
        }
        target?.focus?.();
    };

    /** The same arrow of the moved game at its new place, or the other one where the list ends. */
    const moveArrow = (userGameId, delta) => {
        const row = [...root.querySelectorAll('[data-rankings-slot]')]
            .find((candidate) => candidate.dataset.userGameId === userGameId);
        const arrows = [...(row?.querySelectorAll?.('[data-rankings-move-delta]') ?? [])];
        const same = arrows.find((arrow) => arrow.dataset.rankingsMoveDelta === delta);

        return same !== undefined && !same.disabled ? same : arrows.find((arrow) => !arrow.disabled) ?? null;
    };

    /**
     * Re-reads the screen from the server and keeps the blocks that can have changed. The rendered state is
     * the only state: nothing here builds a row out of a JSON payload, so what is on screen after an
     * operation cannot drift from what was stored.
     *
     * Answers `ok`, `failed` or `stale`. `failed` covers a request that never arrived and a 200 that is not
     * this screen - an expired session rendering the sign-in page, an error page - because those parse
     * perfectly well and committing one would leave foreign markup under the word `Saved`. `stale` means a
     * newer refresh has already started and will commit instead; the caller must stay quiet about it.
     */
    /**
     * The newest read failed, so the list on screen stays - and so must the order it was read in. A select
     * left on an order that never arrived cannot be chosen again (no `change`), and a later read would ask
     * for an order nobody sees. Whichever read was superseded on the way here does not matter.
     */
    const failRead = (results) => {
        results?.removeAttribute?.('aria-busy');
        if (pickerSort !== shownSort) {
            pickerSort = shownSort;
            if (sortSelect !== null) {
                sortSelect.value = shownSort;
            }
            // Aiming a position while the read was pending already wrote the order that never arrived into
            // the address; a reload must bring back the list that stayed.
            historyApi?.replaceState?.({}, '', buildUrl(currentParameters()));
        }

        return 'failed';
    };

    const refresh = async (swapPositions, requested = currentParameters()) => {
        const url = buildUrl(requested);
        // Only the newest request may commit. Two searches in flight can answer in either order, and the
        // slower one would otherwise overwrite the results of the query still in the field.
        const generation = ++refreshGeneration;
        const selectionAtStart = selectionRevision;
        const results = root.querySelector('[data-rankings-picker-results]');
        // A newer search takes over the same surface. Cancelling here also removes WAAPI's `fill: both`,
        // so an interrupted fade cannot leave the list translucent or pinned to an obsolete height.
        cancelAnimations(pickerAnimations);
        pickerAnimations = [];
        results?.setAttribute?.('aria-busy', 'true');

        let nextDocument = null;
        try {
            const response = await fetchImpl(url, {
                headers: { Accept: 'text/html' },
                credentials: 'same-origin',
            });
            if (response.ok) {
                nextDocument = parseHtml(await response.text());
            }
        } catch (_error) {
            nextDocument = null;
        }

        if (generation !== refreshGeneration) {
            return 'stale';
        }

        if (nextDocument === null) {
            return failRead(results);
        }

        // ponytail: the whole page is fetched and two blocks of it are kept. A dedicated fragment endpoint
        // would save the rest of the bytes; add one if this page ever gets heavy enough to notice.
        const nextResults = nextDocument.querySelector('[data-rankings-picker-results]');
        const nextPositions = swapPositions === true
            ? nextDocument.querySelector('[data-rankings-positions]')
            : null;
        const nextCount = swapPositions === true
            ? nextDocument.querySelector('[data-rankings-count]')
            : null;
        if (
            !(results instanceof HTMLElement)
            || !(nextResults instanceof HTMLElement)
            || (swapPositions === true && !(nextPositions instanceof HTMLElement))
            || (swapPositions === true && !(nextCount instanceof HTMLElement))
        ) {
            return failRead(results);
        }

        const animateSwap = canAnimateElement(results) && canAnimateElement(nextResults);
        const duration = readCssTimeMs(results, '--rankings-picker-swap-duration', 170);
        const previousHeight = Number(results.getBoundingClientRect?.().height ?? 0);
        const outgoingAnimations = animateSwap
            ? [playAnimation(results, [
                { opacity: 1, transform: 'translateY(0)' },
                { opacity: 0, transform: 'translateY(-4px)' },
            ], {
                duration: Math.min(duration, 100),
                easing: 'ease-in',
                fill: 'both',
            })]
            : [];
        pickerAnimations = outgoingAnimations;
        await settleAnimations(outgoingAnimations);
        if (generation !== refreshGeneration) {
            cancelAnimations(outgoingAnimations);

            return 'stale';
        }
        cancelAnimations(outgoingAnimations);
        pickerAnimations = [];
        results.removeAttribute('aria-busy');
        results.replaceWith(nextResults);
        // Recorded with the swap, not after the entrance animation: from here on this order is on screen,
        // and a read that fails while the list is still sliding in must fall back to it.
        shownSort = requested.sort;

        const nextHeading = nextDocument.querySelector('[data-rankings-assign-heading]');
        if (heading instanceof HTMLElement && nextHeading instanceof HTMLElement) {
            heading.textContent = nextHeading.textContent;
        }

        if (nextPositions instanceof HTMLElement) {
            const positions = root.querySelector('[data-rankings-positions]');
            if (positions instanceof HTMLElement) {
                // The entrance animation is an entrance, not a re-render: a swapped-in list opts out of it
                // for the same reason a dragged one does.
                nextPositions.classList.add('is-reordered');
                carryCategoryDrafts(positions, nextPositions);
                positions.replaceWith(nextPositions);
                initReorder();
            }
        }

        const count = root.querySelector('[data-rankings-count]');
        if (count instanceof HTMLElement && nextCount instanceof HTMLElement) {
            count.textContent = nextCount.textContent;
        }

        // The delete confirmation names the placed games, so it follows the same swap as the count. Absent
        // on a transient predefined ranking, which has nothing to delete.
        const deleteDescription = root.querySelector('[data-rankings-delete-description]');
        const nextDeleteDescription = swapPositions === true
            ? nextDocument.querySelector('[data-rankings-delete-description]')
            : null;
        if (deleteDescription instanceof HTMLElement && nextDeleteDescription instanceof HTMLElement) {
            deleteDescription.textContent = nextDeleteDescription.textContent;
        }

        // Whose answer wins depends on whether anything was chosen while this was in flight - and whether that
        // choice is still a free position in the markup now on screen. The operation being re-read may have
        // filled the very slot chosen again meanwhile, and aiming at it would turn the next `Add` into a
        // silent replace.
        const chosenRow = selectionRevision === selectionAtStart
            ? null
            : [...root.querySelectorAll('[data-rankings-slot]')]
                .find((row) => toPosition(row.dataset.position) === selectedPosition);
        if (!(chosenRow?.classList?.contains('is-empty') === true)) {
            // Nothing was, so the response is the whole truth - including a target it moved. The position
            // that was aimed at may have just been filled by this very operation, or have stopped existing
            // (a lowered position count, a deleted category) since this screen was rendered; re-asserting a
            // local value over that would aim the picker at a slot the next `Add` can only be rejected for.
            rememberSelection(toPosition(
                nextDocument.querySelector('[data-rankings-edit]')?.dataset?.rankingsSelectedPosition,
            ));
        } else {
            // Something was, and this markup is one click out of date - silently so, because it brings its
            // own hidden position input. The newest choice wins over it.
            applySelection(selectedPosition);
        }

        // The request's own picker state, because that is what the committed markup shows, plus the position
        // as it settled. Recomputing the whole thing here would stamp a query whose results are still coming.
        // Written with the markup, not after its entrance: a newer read can still overtake the animation and
        // fail, and the address must then describe the list that stays on screen.
        historyApi?.replaceState?.({}, '', buildUrl({ ...requested, position: selectedPosition }));

        if (animateSwap) {
            const nextHeight = Number(nextResults.getBoundingClientRect?.().height ?? 0);
            const incomingAnimations = [
                playAnimation(nextResults, [
                    { opacity: 0, transform: 'translateY(4px)' },
                    { opacity: 1, transform: 'translateY(0)' },
                ], {
                    duration,
                    easing: 'cubic-bezier(0.16, 1, 0.3, 1)',
                    fill: 'both',
                }),
            ];
            if (previousHeight > 0 && nextHeight > 0 && previousHeight !== nextHeight) {
                incomingAnimations.push(playAnimation(nextResults, [
                    { height: previousHeight + 'px', overflow: 'hidden' },
                    { height: nextHeight + 'px', overflow: 'hidden' },
                ], {
                    duration,
                    easing: 'cubic-bezier(0.16, 1, 0.3, 1)',
                    fill: 'both',
                }));
            }
            pickerAnimations = incomingAnimations;
            await settleAnimations(incomingAnimations);
            cancelAnimations(incomingAnimations);
            if (pickerAnimations === incomingAnimations) {
                pickerAnimations = [];
            }
            if (generation !== refreshGeneration) {
                return 'stale';
            }
        }

        return 'ok';
    };

    /**
     * One operation, stored immediately. On failure nothing changed, so the row's own control is the retry.
     *
     * The lock is held across the re-read, not only across the write: until the new markup is in place the
     * positions and the picker on screen are the ones from before the operation, and a second submit from
     * them would address a slot by a number that no longer means the same thing - overwriting the placement
     * that just succeeded.
     *
     * Every outcome is reported, including the ones whose result is visible anyway. A game appearing in a
     * slot is stronger feedback than the word next to it - but only to someone who can see it: the rows are
     * ordinary DOM, so nothing announces them, and going from `Saving...` to silence leaves a screen reader
     * with an operation that never finished. The confirmation expires either way.
     *
     * `savedMessage` names what happened (`{ target: 'Added to #3', title }`), captured by the caller before
     * the write, while the rows still show it. `aimFirstFree` re-reads without a position, so the server aims
     * the picker at the first free slot - an `Add` filled the old target and a `Remove` may have freed a lower
     * one. `focusAfter` answers where focus goes once the new markup is in place; `flashPosition` is the row
     * that received a game. `failureMessage` names the operation when the server sends no message of its own.
     */
    const mutate = async (url, body, {
        failureMessage,
        row = null,
        onStored = null,
        savedMessage = '',
        aimFirstFree = false,
        focusAfter = null,
        flashPosition = null,
    } = {}) => {
        if (isRankingBusy(root) || typeof url !== 'string' || url === '') {
            return;
        }

        setRankingBusy(root, true);
        const focusAtStart = focusRevision;
        showStatus('Saving...', 'saving');
        try {
            const result = await sendJsonMutation(fetchImpl, {
                url,
                csrfToken: resolveCsrfToken(root),
                body,
                fallbackErrorMessage: failureMessage,
            });

            if (!result.ok) {
                row?.classList?.add('is-error');
                showStatus(result.errorMessage, 'error');

                return;
            }

            row?.classList?.remove('is-error');
            onStored?.();
            // The write landed either way, but reporting it as stored also promises that what is on screen
            // is the result of it. Anything other than a committed re-read breaks that promise and says so.
            const refreshed = await refresh(
                true,
                aimFirstFree ? { ...currentParameters(), position: null } : currentParameters(),
            );
            if (refreshed === 'ok') {
                restoreFocus(focusAfter?.(), focusAtStart);
                if (flashPosition !== null) {
                    flashRow(flashPosition);
                }
                showStatus(
                    savedMessage === '' ? 'Saved' : savedMessage,
                    'saved',
                    savedMessage === '' ? SAVED_VISIBLE_MS : NAMED_VISIBLE_MS,
                );

                return;
            }

            // Stored, and the screen no longer describes what was stored. Asking for a reload is not enough
            // on its own: the positions still on screen would let the next `Add` overwrite this write, so
            // the lock is kept and every later operation is refused instead of being allowed to do damage.
            markRankingStale(root);
            showStatus(REFRESH_FAILURE_MESSAGE, 'error');
        } finally {
            setRankingBusy(root, false);
        }
    };

    /**
     * Writes a chosen position across everything that reads it. Separate from `select()` so a swapped-in
     * results block can be brought back in line with the choice: the server rendered that block from the URL
     * the request carried, which is one click out of date whenever a position was picked while it was in
     * flight.
     */
    const applySelection = (position) => {
        let selectedName = '';
        root.querySelectorAll('[data-rankings-slot]').forEach((row) => {
            const isSelected = toPosition(row.dataset.position) === position;
            row.classList.toggle('is-selected', isSelected);
            row.querySelector('[data-rankings-select-position]')
                ?.setAttribute('aria-current', isSelected ? 'true' : 'false');
            if (isSelected) {
                selectedName = slotName(row);
            }
        });

        const positionInput = root.querySelector('[data-rankings-assign-position]');
        if (positionInput !== null) {
            positionInput.value = String(position);
        }
        if (heading instanceof HTMLElement && selectedName !== '') {
            heading.textContent = 'Adding to ' + selectedName;
        }
    };

    /**
     * Focuses `focusTarget` without letting the focus scroll, and scrolls `scrollTarget` into view first -
     * only when `focusTarget` is not already visible between the navigation bars, which the page's own
     * `scroll-padding` describes.
     */
    const reveal = (scrollTarget, focusTarget, block) => {
        const view = root.defaultView ?? globalThis.window;
        const rect = focusTarget?.getBoundingClientRect?.();
        if (rect !== undefined && view !== undefined && scrollTarget instanceof HTMLElement) {
            const padding = view.getComputedStyle?.(root.documentElement);
            const topInset = Number.parseFloat(padding?.scrollPaddingTop ?? '0') || 0;
            const bottomInset = Number.parseFloat(padding?.scrollPaddingBottom ?? '0') || 0;
            if (rect.top < topInset || rect.bottom > view.innerHeight - bottomInset) {
                scrollTarget.scrollIntoView({
                    block,
                    behavior: prefersReducedMotion(view) ? 'instant' : 'smooth',
                });
            }
        }
        // This module placed it already, possibly with a smooth scroll still running; the focus correction
        // below must not cut that scroll short.
        placingFocus = true;
        try {
            focusTarget?.focus?.({ preventScroll: true });
        } finally {
            placingFocus = false;
        }
    };

    const select = (position) => {
        if (position === null) {
            return;
        }

        selectionRevision += 1;
        // The attribute, not the hidden assign input: the input lives inside the block every search
        // replaces, while the renumbering after a reorder needs the aimed position wherever it reads from.
        rememberSelection(position);
        applySelection(position);
        historyApi?.replaceState?.({}, '', buildUrl(currentParameters()));
        // On a phone the picker is under every position. Focus goes to its heading, never to the search,
        // so choosing a position does not open the keyboard over the list it is about to fill.
        reveal(heading?.closest?.('section') ?? heading, heading, 'start');
    };

    /**
     * The way back up from the candidates on a phone: to the row being filled, with its own aiming control
     * focused, or to the `Positions` heading when nothing is aimed at (every position is taken).
     */
    const backToPositions = () => {
        const row = [...root.querySelectorAll('[data-rankings-slot]')]
            .find((candidate) => toPosition(candidate.dataset.position) === selectedPosition);
        const aimControl = row?.querySelector?.('[data-rankings-select-position]');
        if (aimControl instanceof HTMLElement) {
            reveal(row, aimControl, 'center');

            return;
        }

        const positionsHeading = root.querySelector('[data-rankings-positions-heading]');
        reveal(positionsHeading?.closest?.('section') ?? positionsHeading, positionsHeading, 'start');
    };

    // Drag-to-reorder is only reachable from here, so this module owns its lifetime: every refresh of the
    // positions list replaces the element the old listeners were bound to.
    initReorder();

    /**
     * A search that never arrived has to say so: silently keeping the previous results under a new query is
     * the one outcome the user cannot tell apart from "nothing matches".
     */
    const runSearch = async (failureMessage = SEARCH_FAILURE_MESSAGE) => {
        // Nothing is coming that would make this screen current again, so waiting for its turn would only
        // re-arm this timer forever. The message already says the page has to be reloaded.
        if (isRankingStale(root)) {
            return;
        }

        // A stored operation owns its re-read: it is the only thing that brings the positions list back in
        // step with what is now in the database. A search starting mid-flight would bump the generation the
        // mutation's refresh checks, leave the positions showing the pre-operation state, and let the next
        // operation address a slot by a number that no longer means the same thing. So it waits its turn.
        if (isRankingBusy(root)) {
            scheduleSearch(failureMessage);

            return;
        }

        // A new query or a new order is a new list, so it starts at its own first window.
        pickerPage = 1;
        const result = await refresh(false);
        if (result === 'failed') {
            showFeedback(failureMessage, true);
        } else if (result === 'ok') {
            showFeedback('', false);
        }
    };

    const runPickerPage = async (page) => {
        if (page === null || isRankingStale(root)) {
            return;
        }
        if (isRankingBusy(root)) {
            if (pickerPageHandle !== null) {
                timerApi.clearTimeout(pickerPageHandle);
            }
            pickerPageHandle = timerApi.setTimeout(() => {
                pickerPageHandle = null;
                void runPickerPage(page);
            }, SEARCH_DEBOUNCE_MS);

            return;
        }

        const result = await refresh(false, { ...currentParameters(), page });
        if (result === 'failed') {
            showFeedback(PAGE_FAILURE_MESSAGE, true);
        } else if (result === 'ok') {
            pickerPage = page;
            showFeedback('', false);
        }
    };

    const scheduleSearch = (failureMessage = SEARCH_FAILURE_MESSAGE) => {
        if (debounceHandle !== null) {
            timerApi.clearTimeout(debounceHandle);
        }
        debounceHandle = timerApi.setTimeout(() => {
            debounceHandle = null;
            void runSearch(failureMessage);
        }, SEARCH_DEBOUNCE_MS);
    };

    /**
     * A focused control never ends up under a pinned panel header. The browser only scrolls a control that
     * is outside the viewport, and one moved to by Shift+Tab is usually inside it - just under the header,
     * or right under it after the browser's own scroll - so the rest is corrected here, against the header
     * as tall as it is right now (a long status makes it taller). After the browser's own focus scroll, so
     * that one cannot undo this.
     */
    editRoot.addEventListener('focusin', (event) => {
        focusRevision += 1;
        const target = event.target;
        const panel = target?.closest?.('.rankings-panel');
        const header = panel?.querySelector?.('.rankings-panel-header');
        if (
            placingFocus
            || !(target instanceof HTMLElement)
            || !(header instanceof HTMLElement)
            || header.contains(target)
        ) {
            return;
        }
        const view = root.defaultView ?? globalThis.window;
        view?.requestAnimationFrame?.(() => {
            // Focus moved on in the meantime - a tap on `+ Add game` hands it to the picker heading and starts
            // the scroll there - so this control is no longer the one to keep in view.
            if (root.activeElement !== target || !target.isConnected) {
                return;
            }
            const gap = 8;
            const headerRect = header.getBoundingClientRect();
            const targetRect = target.getBoundingClientRect();
            // Only a control that is (partly) behind the header; one entirely above it is off screen, and
            // bringing it back is the browser's own focus scroll.
            const overlap = targetRect.bottom > headerRect.top ? headerRect.bottom + gap - targetRect.top : 0;
            // The picker scrolls inside itself on desktop; everywhere else the page does.
            const scrollsItself = panel.scrollHeight > panel.clientHeight
                && view.getComputedStyle(panel).overflowY !== 'visible';
            if (overlap > 0) {
                if (scrollsItself) {
                    panel.scrollTop -= overlap;
                } else {
                    view.scrollBy({ top: -overlap, behavior: 'instant' });
                }
            }
            // Near the end of the page the pinned picker is pushed up with the end of its row, header and
            // all, until the navigation covers them; the page then moves back down enough to show both.
            const navCover = Number.parseFloat(view.getComputedStyle(root.documentElement).scrollPaddingTop) || 0;
            const underNav = navCover - header.getBoundingClientRect().top;
            if (scrollsItself && underNav > 0) {
                view.scrollBy({ top: -underNav, behavior: 'instant' });
            }
        });
    });

    // A drag is not the keyboard move whose arrow is waiting for focus.
    editRoot.addEventListener('dragstart', () => {
        moveFocus = null;
    });

    editRoot.addEventListener('click', (event) => {
        const target = event.target;
        if (!(target instanceof HTMLElement)) {
            return;
        }

        // Moves nothing and writes nothing, so it works on a stale screen too.
        if (target.closest('[data-rankings-back-to-positions]') instanceof HTMLElement) {
            backToPositions();

            return;
        }

        // Nothing is intercepted on a stale screen. The position link is a real link - swallowing it to move
        // a local marker instead is exactly what turned it into a dead control - and letting it navigate
        // re-renders the screen from the server, which is the way out of this state.
        if (isRankingStale(root)) {
            return;
        }

        const pageLink = target.closest('[data-rankings-picker-page-link]');
        if (pageLink instanceof HTMLElement) {
            event.preventDefault();
            void runPickerPage(toPosition(pageLink.dataset.rankingsPickerPageLink));

            return;
        }

        const selectLink = target.closest('[data-rankings-select-position]');
        if (selectLink instanceof HTMLElement) {
            event.preventDefault();
            select(toPosition(selectLink.dataset.rankingsSelectPosition));

            return;
        }

        // An empty row reads as one thing, so all of it aims the picker - not only the number on it.
        const row = target.closest('[data-rankings-slot]');
        if (
            row instanceof HTMLElement
            && row.classList.contains('is-empty')
            && target.closest('a') === null
            && target.closest('button') === null
            // A custom category row holds its rename form: typing a name is not aiming the picker.
            && target.closest('form') === null
            && target.closest('label') === null
        ) {
            select(toPosition(row.dataset.position));
        }
    });

    editRoot.addEventListener('submit', (event) => {
        const form = event.target;
        if (!(form instanceof HTMLElement)) {
            return;
        }

        if (form.dataset.rankingsSearchForm !== undefined) {
            // Same reason: the form is an ordinary GET. On a stale screen its own submit is the fix.
            if (isRankingStale(root)) {
                return;
            }

            event.preventDefault();
            if (debounceHandle !== null) {
                timerApi.clearTimeout(debounceHandle);
                debounceHandle = null;
            }
            void runSearch();

            return;
        }

        if (form.dataset.rankingsAssignForm !== undefined) {
            if (navigates) {
                return;
            }

            event.preventDefault();
            const userGameId = String(event.submitter?.value ?? '').trim();
            const position = toPosition(root.querySelector('[data-rankings-assign-position]')?.value);
            if (userGameId === '' || position === null) {
                showFeedback('Choose a position for this game first.', true);

                return;
            }

            // Named before the write: the re-read replaces both the candidate and the heading.
            const title = textOf(event.submitter?.closest?.('[data-rankings-picker-item]')
                ?.querySelector?.('.rankings-picker-title'));
            const target = slotNameAt(root, position);
            void mutate(form.getAttribute('action'), { position, user_game_id: userGameId }, {
                failureMessage: ADD_FAILURE_MESSAGE,
                savedMessage: title !== '' && target !== '' ? { target: 'Added to ' + target, title } : '',
                aimFirstFree: true,
                // The heading names the next target, which is what the user needs to know next.
                focusAfter: () => heading,
                flashPosition: position,
            });

            return;
        }

        if (form.dataset.rankingsClearForm !== undefined) {
            event.preventDefault();
            const row = form.closest('[data-rankings-slot]');
            // Named before the write: the re-read replaces the row that says what was there.
            const title = textOf(row?.querySelector?.('.rankings-slot-game-title'));
            const target = row instanceof HTMLElement ? slotName(row) : '';
            const position = toPosition(row?.dataset.position);
            void mutate(form.getAttribute('action'), { position }, {
                failureMessage: REMOVE_FAILURE_MESSAGE,
                row,
                savedMessage: title !== '' && target !== '' ? { target: 'Removed from ' + target, title } : '',
                aimFirstFree: true,
                // The emptied row's own aiming control: the place just freed, whatever the picker aims at.
                focusAfter: () => slotAt(root, position)?.querySelector?.('[data-rankings-select-position]'),
            });

            return;
        }

        if (form.dataset.rankingsRenameForm !== undefined) {
            // Answered in place like every other operation here, so a name typed into another category
            // is still there afterwards; the form's own POST stays the path without this module.
            event.preventDefault();
            const label = formValue(form, 'label');
            const input = form.querySelector('input[name="label"]');
            void mutate(form.getAttribute('action'), {
                position: toPosition(formValue(form, 'position')),
                label,
            }, {
                failureMessage: RENAME_FAILURE_MESSAGE,
                row: form.closest('[data-rankings-slot]'),
                onStored: () => {
                    // The stored name is the baseline from here on, so anything typed while the rows are
                    // re-read - even the old name back - is a draft of its own.
                    if (input instanceof HTMLInputElement) {
                        input.defaultValue = label;
                    }
                },
            });

            return;
        }

        if (form.dataset.rankingsMoveForm !== undefined) {
            event.preventDefault();
            const row = form.closest('[data-rankings-slot]');
            const targetPosition = toPosition(event.submitter?.value);
            const userGameId = formValue(form, 'user_game_id');
            const delta = String(event.submitter?.dataset?.rankingsMoveDelta ?? '');
            moveFocus = { userGameId, delta, focusAtStart: focusRevision };
            if (
                row instanceof HTMLElement
                && targetPosition !== null
                && reorderController?.moveRow(row, targetPosition - 1) === true
            ) {
                return;
            }
            moveFocus = null;

            void mutate(form.getAttribute('action'), {
                position: toPosition(formValue(form, 'position')),
                to_position: targetPosition,
                // The game the control named when it was rendered. A concurrent reorder changes what sits
                // at a position, and the server refuses to move anything else.
                user_game_id: userGameId,
            }, { failureMessage: MOVE_FAILURE_MESSAGE, row, focusAfter: () => moveArrow(userGameId, delta) });
        }
    });

    if (searchInput !== null) {
        searchInput.addEventListener('input', () => {
            query = String(searchInput.value ?? '').trim();
            if (searchClear instanceof HTMLElement) {
                searchClear.hidden = query === '';
            }
            scheduleSearch();
        });
    }

    if (sortSelect !== null) {
        // The query and the aimed position stay; only the order, and with it the window, changes. A native
        // submit of the search form carries the select too, so this only saves the round trip.
        sortSelect.addEventListener('change', () => {
            pickerSort = String(sortSelect.value ?? '').trim() || DEFAULT_PICKER_SORT;
            if (debounceHandle !== null) {
                timerApi.clearTimeout(debounceHandle);
                debounceHandle = null;
            }
            void runSearch(SORT_FAILURE_MESSAGE);
        });
    }

    if (searchClear instanceof HTMLElement) {
        searchClear.addEventListener('click', () => {
            if (searchInput !== null) {
                searchInput.value = '';
                searchInput.focus();
            }
            searchClear.hidden = true;
            if (query === '') {
                return;
            }
            query = '';
            void runSearch();
        });
    }

    // The settings panel is outside the editing root, under every position. Its open state belongs to the
    // shared disclosure, so this asks it through its own trigger instead of setting `open` itself.
    const manageLink = root.querySelector('[data-rankings-manage-open]');
    const managePanel = root.querySelector('[data-rankings-manage]');
    const manageTrigger = managePanel?.querySelector?.('.ui-disclosure-trigger');
    const manageContent = managePanel?.querySelector?.('.ui-disclosure-content');
    let scrollAfterOpen = null;
    if (manageLink instanceof HTMLElement && manageTrigger instanceof HTMLElement) {
        manageLink.addEventListener('click', (event) => {
            event.preventDefault();
            const view = root.defaultView ?? globalThis.window;
            const reducedMotion = prefersReducedMotion(view);
            const scrollToPanel = () => managePanel.scrollIntoView({
                block: 'start',
                behavior: reducedMotion ? 'instant' : 'smooth',
            });
            if (scrollAfterOpen !== null) {
                manageContent.removeEventListener('transitionend', scrollAfterOpen);
                scrollAfterOpen = null;
            }
            // A closed panel is still collapsed when the click that opens it ends, and the page under it is
            // too short to bring it to the top: the scroll waits for the content to finish growing. Only the
            // content's own row transition counts - its body fades in too, and that event bubbles here.
            if (!managePanel.open && !reducedMotion && manageContent instanceof HTMLElement) {
                scrollAfterOpen = (transition) => {
                    if (transition.target !== manageContent || transition.propertyName !== 'grid-template-rows') {
                        return;
                    }
                    manageContent.removeEventListener('transitionend', scrollAfterOpen);
                    scrollAfterOpen = null;
                    scrollToPanel();
                };
                manageContent.addEventListener('transitionend', scrollAfterOpen);
                manageTrigger.click();
            } else {
                if (!managePanel.open) {
                    manageTrigger.click();
                }
                scrollToPanel();
            }
            // Shown as keyboard focus even after a tap: the ring is what says where the shortcut went.
            manageTrigger.focus({ preventScroll: true, focusVisible: true });
        });
    }
}

function toPosition(value) {
    const parsed = Number.parseInt(String(value ?? ''), 10);

    return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
}

/**
 * A category name typed but not renamed yet outlives a re-read of the rows. Nothing answered in place moves
 * a category to another position, so the position is the key. A rename that landed has already moved its
 * row's baseline to the stored name, so it is no draft.
 */
function carryCategoryDrafts(positions, nextPositions) {
    const drafts = new Map();
    positions.querySelectorAll('[data-rankings-slot]').forEach((row) => {
        const input = row.querySelector('[data-rankings-category-input]');
        if (input instanceof HTMLInputElement && input.value !== input.defaultValue) {
            drafts.set(row.dataset.position, input.value);
        }
    });
    nextPositions.querySelectorAll('[data-rankings-slot]').forEach((row) => {
        const input = row.querySelector('[data-rankings-category-input]');
        const draft = drafts.get(row.dataset.position);
        // The server trims a name, so a draft equal to the stored one after trimming is the rename that landed.
        if (input instanceof HTMLInputElement && draft !== undefined && draft.trim() !== input.defaultValue) {
            input.value = draft;
        }
    });
}

function formValue(form, name) {
    return String(form.querySelector('input[name="' + name + '"]')?.value ?? '').trim();
}

/**
 * `#4` for a ranking position, the category name for a category - the stored one, not a draft typed into
 * its rename field.
 */
function slotName(row) {
    const label = row.querySelector('.rankings-slot-label-input') ?? row.querySelector('.rankings-slot-label');
    if (label !== null && label !== undefined) {
        return String(label.defaultValue ?? label.textContent ?? '').trim();
    }

    return String(row.querySelector('.rankings-slot-position')?.textContent ?? '').trim();
}

function slotAt(root, position) {
    return [...root.querySelectorAll('[data-rankings-slot]')]
        .find((candidate) => toPosition(candidate.dataset.position) === position) ?? null;
}

function slotNameAt(root, position) {
    const row = slotAt(root, position);

    return row instanceof HTMLElement ? slotName(row) : '';
}

function textOf(element) {
    return String(element?.textContent ?? '').trim();
}
